/**
 * Live event-stream provider + hooks.
 *
 * Backed by the backend SSE endpoint at ``/api/admin/events/stream``.
 * One ``EventSource`` per app lifetime, owned by ``EventStreamProvider``
 * mounted in AppShell. Consumers subscribe via ``useLiveEvents()`` —
 * they get the rolling buffer (newest first, capped) and the live
 * connection state.
 *
 * Why one global EventSource instead of per-component?
 *   - SSE costs a long-lived HTTP connection per source. Sharing keeps
 *     the count to 1 even with N consumers.
 *   - The buffer is a single source of truth — notification bell,
 *     activity feed, topology pulses all see the same events.
 *
 * Auth note: ``EventSource`` doesn't support custom headers, so the
 * token rides as a query parameter. Backend ``current_user`` accepts
 * tokens from either ``Authorization`` header or ``?token=`` query.
 */
import * as React from "react";

import { useAuthStore } from "@/lib/auth";


// ---------------------------------------------------------------------------
// Event shape (mirrors backend hub.events.types.Event subclasses)
// ---------------------------------------------------------------------------

export type EventKind =
  | "execution_started"
  | "execution_finished"
  // Per-node lifecycle inside one execution. Fires from the executor
  // immediately before/after each handler call so the editor canvas
  // can light up the active node in real-time.
  | "node_started"
  | "node_finished"
  // Throttled per-node progress ping (ADR-0067 S2): a code/loop node
  // processing a large batch emits `done`/`total` so the test-run overlay
  // shows "item 142.000 / 381.609" instead of a frozen spinner.
  | "node_progress"
  | "message_received"
  | "workflow_enabled_changed"
  // Refresh signal — sent when a new notification is INSERTed on the
  // backend. The frontend reacts by invalidating its notifications
  // query; no per-notification details ride on this event.
  | "notification_created"
  // ETL run lifecycle + live per-node metrics (ADR-0038). The designer
  // overlays node status rings from these; `node_metrics` rides on
  // METRICS/FINISHED.
  | "etl_run_started"
  | "etl_run_finished"
  | "etl_run_metrics";

export type Severity = "info" | "success" | "warning" | "error";

export interface LiveEvent {
  id: string;
  kind: EventKind;
  ts: string;
  actor: string;
  severity: Severity;
  title: string;
  subtitle: string | null;
  /** Subclass-specific extras (workflow_id, execution_id, status, etc.). */
  [extra: string]: unknown;
}

export type ConnectionState = "connecting" | "open" | "closed";


// ---------------------------------------------------------------------------
// Provider state
// ---------------------------------------------------------------------------

interface EventStreamState {
  events: LiveEvent[];
  state: ConnectionState;
  /** Bump on each new event so subscribers can derive memoised selectors. */
  generation: number;
}

interface EventStreamCtx extends EventStreamState {
  clear: () => void;
}

const Ctx = React.createContext<EventStreamCtx | null>(null);

/** Rolling buffer cap. Old events drop off the bottom. */
const BUFFER_SIZE = 200;


export function EventStreamProvider({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.token);
  const [state, setState] = React.useState<EventStreamState>({
    events: [],
    state: "closed",
    generation: 0,
  });

  React.useEffect(() => {
    if (!token) {
      // Logged out — no stream.
      setState((s) => ({ ...s, state: "closed" }));
      return;
    }

    setState((s) => ({ ...s, state: "connecting" }));
    const url = `/api/admin/events/stream?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);

    es.onopen = () => {
      setState((s) => ({ ...s, state: "open" }));
    };

    es.onerror = () => {
      // The browser auto-reconnects; we just reflect transient state.
      setState((s) => ({ ...s, state: "connecting" }));
    };

    // The backend tags each record with ``event:`` so consumers could
    // subscribe per-kind. We funnel everything through one handler to
    // keep the buffer authoritative.
    es.onmessage = (e: MessageEvent) => {
      try {
        const evt = JSON.parse(e.data) as LiveEvent;
        setState((prev) => {
          // Prepend (newest first), cap at BUFFER_SIZE.
          const events = [evt, ...prev.events].slice(0, BUFFER_SIZE);
          return { events, state: "open", generation: prev.generation + 1 };
        });
      } catch {
        /* malformed line, skip */
      }
    };
    // ``event:`` named records bypass ``onmessage`` and need an
    // explicit listener. We re-dispatch through the same path.
    const kinds: EventKind[] = [
      "execution_started",
      "execution_finished",
      "node_started",
      "node_finished",
      "node_progress",
      "message_received",
      "workflow_enabled_changed",
      "notification_created",
      "etl_run_started",
      "etl_run_finished",
      "etl_run_metrics",
    ];
    const handler = (e: MessageEvent) => {
      try {
        const evt = JSON.parse(e.data) as LiveEvent;
        setState((prev) => {
          const events = [evt, ...prev.events].slice(0, BUFFER_SIZE);
          return { events, state: "open", generation: prev.generation + 1 };
        });
      } catch {
        /* skip */
      }
    };
    kinds.forEach((k) => es.addEventListener(k, handler as EventListener));

    return () => {
      kinds.forEach((k) => es.removeEventListener(k, handler as EventListener));
      es.close();
    };
  }, [token]);

  const clear = React.useCallback(() => {
    setState((s) => ({ ...s, events: [] }));
  }, []);

  const value = React.useMemo(() => ({ ...state, clear }), [state, clear]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}


// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useLiveEvents(): EventStreamCtx {
  const ctx = React.useContext(Ctx);
  if (!ctx) {
    throw new Error("useLiveEvents must be used inside <EventStreamProvider>");
  }
  return ctx;
}

/** Filter the live buffer by predicate, memoised on the generation counter. */
export function useFilteredEvents(
  predicate: (e: LiveEvent) => boolean,
): LiveEvent[] {
  const { events, generation } = useLiveEvents();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return React.useMemo(() => events.filter(predicate), [generation, predicate]);
}
